import { getStartOfDay, parseDate } from "../../shared/lib/dates";
import { formatPrice } from "../../shared/lib/format-price";
import { ChargesFilterSearch } from "../../pages/charges-page/types";
import { RentAreaListItem } from "../rent-area/types";

import {
  CHARGE_STATUS_OPTIONS,
  CHARGE_TYPE_OPTIONS,
  TENANT_VISIBLE_CHARGE_STATUSES,
} from "./constants";
import {
  ChargeItem,
  ChargeListItem,
  ChargeRentAreaFilterOption,
  ChargesFilterFormValues,
  GetChargesFilter,
} from "./types";

export const parseFilterSearchToRequestDto = (
  filter: ChargesFilterSearch,
): GetChargesFilter => {
  return {
    statuses: filter.statuses?.length
      ? filter.statuses
      : [...TENANT_VISIBLE_CHARGE_STATUSES],
    rentAreaIds: filter.rentAreas?.length ? filter.rentAreas : undefined,
    types: filter.types?.length ? filter.types : undefined,
  };
};

export const parseFilterFormValuesToFilterSearch = (
  values: ChargesFilterFormValues,
): ChargesFilterSearch => {
  return {
    statuses: values.statuses?.map(({ value }) => value),
    rentAreas: values.rentAreas?.map(({ rentAreaId }) => rentAreaId),
    types: values.types?.map(({ value }) => value),
  };
};

export const parseFilterSearchToFormValues = ({
  filter,
  rentAreaOptions = [],
}: {
  filter: ChargesFilterSearch;
  rentAreaOptions?: NonNullable<ChargesFilterFormValues["rentAreas"]>;
}): ChargesFilterFormValues => {
  return {
    statuses:
      filter.statuses
        ?.map((status) =>
          CHARGE_STATUS_OPTIONS.find((option) => option.value === status),
        )
        .filter(Boolean) || [],
    rentAreas:
      filter.rentAreas
        ?.map((rentAreaId) =>
          rentAreaOptions.find((option) => option.rentAreaId === rentAreaId),
        )
        .filter(Boolean) || [],
    types:
      filter.types
        ?.map((type) =>
          CHARGE_TYPE_OPTIONS.find((option) => option.value === type),
        )
        .filter(Boolean) || [],
  };
};

type RentAreaFilterOptionSource = Pick<
  RentAreaListItem,
  "project" | "rawAddress" | "rentAreaId" | "rentAreaName" | "unit"
>;

export const mapRentAreaToFilterOption = ({
  project,
  rawAddress,
  rentAreaId,
  rentAreaName,
  unit,
}: RentAreaFilterOptionSource): ChargeRentAreaFilterOption => ({
  rentAreaId,
  unit: unit || rentAreaName || rawAddress || rentAreaId,
  projectName: project.name || null,
});

type ChargeDocumentSource = Pick<
  ChargeListItem | ChargeItem,
  "amount" | "chargeDate" | "dueDate" | "number"
>;

type ChargePaymentStatusSource = Pick<
  ChargeListItem | ChargeItem,
  "status" | "dueDate"
>;

const CHARGE_DOCUMENT_STUB_URL = "/charge-invoice-stub.pdf";
const CHARGE_DOCUMENT_STUB_FILE_NAME = "(Пример) Счет№ Д-01-2025_40000 руб.pdf";

const CHARGE_PAYMENT_STATUS_LABELS: Record<string, string> = {
  Paid: "Оплачено",
  Overdue: "Просрочено",
  Unpaid: "Не оплачено",
};

export const formatChargeNumber = (number: number) =>
  `№${number.toString().padStart(7, "0")}`;

export const formatChargeDate = (
  date: string | null | undefined,
  formatString = "dd.MM.yy",
) => (date ? parseDate(date, formatString) : "-");

export const formatChargeAmount = (amount: number | null | undefined) =>
  amount === null || amount === undefined ? "-" : `${formatPrice(amount)} ₽`;

export const formatChargePaymentStatus = (
  paymentStatusCode: string | null | undefined,
) =>
  paymentStatusCode
    ? CHARGE_PAYMENT_STATUS_LABELS[paymentStatusCode] || "-"
    : "-";

export const formatChargeHeadline = (
  typeTitle: string | null | undefined,
  amount: number | null | undefined,
) => `${typeTitle || "Начисление"} - ${formatChargeAmount(amount)}`;

export const isChargePaymentOverdue = (
  { status, dueDate }: ChargePaymentStatusSource,
  currentDate: Date | string = new Date(),
) => {
  if (status.code !== "Issued" || !dueDate) {
    return false;
  }

  return (
    getStartOfDay(currentDate).getTime() > getStartOfDay(dueDate).getTime()
  );
};

export const getChargeDocumentTitle = ({
  number,
  amount,
  dueDate,
  chargeDate,
}: ChargeDocumentSource) =>
  [
    formatChargeNumber(number),
    formatChargeDate(dueDate || chargeDate, "dd.MM.yyyy"),
    formatChargeAmount(amount),
  ].join("_");

export const downloadChargeDocument = (_charge: ChargeDocumentSource) => {
  const link = document.createElement("a");

  link.href = CHARGE_DOCUMENT_STUB_URL;
  link.download = CHARGE_DOCUMENT_STUB_FILE_NAME;
  document.body.append(link);
  link.click();
  link.remove();
};
